import React from 'react';
import WithNavigation from 'src/layouts/WithNavigation/WithNavigation';
import Heading from 'src/components/Heading';
import Button from 'src/components/Button';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import LoadState from 'src/components/LoadState/LoadState';
import Table from 'src/components/Table/Table';
import { ITableColumn } from 'src/components/Table/types';
import { IOutboxEmail } from 'src/services/api/EmailOutbox/apiContracts';
import { useOutboxPageOrchestrator } from 'src/pages/Outbox/CustomHooks/useOutboxPageOrchestrator';
import {
  formatOutboxTimestamp,
  getDeliveryStatusColours,
  getStatusColours,
} from 'src/pages/Outbox/helpers';
import {
  IPillColours,
  IUseOutboxPageOrchestratorReturn,
} from 'src/pages/Outbox/types';
import './outbox.scss';

interface IOutboxEmailCellProps {
  email: IOutboxEmail;
}

const StatusCell = ({ email }: IOutboxEmailCellProps) => {
  const colours: IPillColours = getStatusColours(email.status);

  return (
    <CategoryPill
      text={email.status}
      color={colours.color}
      textColor={colours.textColor}
      size="small"
    />
  );
};

const DeliveryCell = ({ email }: IOutboxEmailCellProps) => {
  if (!email.deliveryStatus) {
    return <span className="outbox-muted">—</span>;
  }

  const colours: IPillColours = getDeliveryStatusColours(email.deliveryStatus);

  return (
    <div className="outbox-delivery">
      <CategoryPill
        text={email.deliveryStatus}
        color={colours.color}
        textColor={colours.textColor}
        size="small"
      />
      <span className="outbox-muted">
        {formatOutboxTimestamp(email.deliveryStatusAt)}
      </span>
    </div>
  );
};

const OUTBOX_COLUMNS: ITableColumn[] = [
  {
    label: 'Created',
    accessor: 'createdOn',
    minWidth: '16rem',
    cell: (email: IOutboxEmail) => formatOutboxTimestamp(email.createdOn),
  },
  { label: 'To', accessor: 'to', minWidth: '20rem' },
  { label: 'Subject', accessor: 'subject', minWidth: '24rem' },
  { label: 'Type', accessor: 'emailType', minWidth: '16rem' },
  {
    label: 'Status',
    accessor: 'status',
    minWidth: '10rem',
    cell: (email: IOutboxEmail) => <StatusCell email={email} />,
  },
  {
    label: 'Delivery',
    accessor: 'deliveryStatus',
    minWidth: '18rem',
    cell: (email: IOutboxEmail) => <DeliveryCell email={email} />,
  },
  { label: 'Attempts', accessor: 'attempts', minWidth: '8rem', center: true },
  {
    label: 'Last error',
    accessor: 'lastError',
    minWidth: '24rem',
    textOverflow: true,
    cell: (email: IOutboxEmail) => (
      <span className="outbox-error">{email.lastError ?? ''}</span>
    ),
  },
];

const Outbox = () => {
  const {
    emails,
    hasMore,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    socketIndicator,
    refresh,
    loadMore,
  }: IUseOutboxPageOrchestratorReturn = useOutboxPageOrchestrator();

  return (
    <WithNavigation
      title="Outbox"
      top={
        <Heading headline="Outbox">
          <div className="outbox-toolbar">
            <CategoryPill
              text={socketIndicator.label}
              color={socketIndicator.color}
              textColor={socketIndicator.textColor}
              size="small"
            />
            <Button
              label={isRefreshing ? 'Refreshing…' : 'Refresh'}
              onClick={refresh}
              disabled={isRefreshing}
              outlined
            />
          </div>
        </Heading>
      }
      bottom={
        <div className="outbox">
          <LoadState
            isLoading={isLoading}
            isEmpty={emails.length === 0}
            error={error}
            emptyNode={
              <div className="outbox-empty">No emails have been sent yet.</div>
            }
          >
            <Table columns={OUTBOX_COLUMNS} data={emails} stickyHeader />
            {hasMore && (
              <div className="outbox-load-more">
                <Button
                  label={isLoadingMore ? 'Loading…' : 'Load more'}
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  outlined
                />
              </div>
            )}
          </LoadState>
        </div>
      }
    />
  );
};

export default Outbox;
